import { Directive, DoCheck, ElementRef, Injectable, Input, NgModule, NgZone, OnDestroy, OnInit, Optional, Self, SkipSelf } from "@angular/core";
import { NgControl } from "@angular/forms";
import { MatButton } from "@angular/material/button";
import { BehaviorSubject, Subscription } from "rxjs";

@Injectable()
export class PiReadOnlyContext {
  readonly active = new BehaviorSubject(false);
}

/** Disable routed workflow controls while retaining explicit view/navigation actions. */
@Directive({ selector: "[vexPiReadOnly]", providers: [PiReadOnlyContext] })
export class PiReadOnlyDirective implements OnDestroy {
  private observer: MutationObserver | null = null;
  private originalDisabled = new Map<HTMLInputElement | HTMLButtonElement | HTMLTextAreaElement | HTMLSelectElement, boolean>();
  private readonly events = ["click", "dblclick", "keydown", "beforeinput", "input", "change", "drop", "dragover", "submit"];
  private readonly viewSelector = "[data-pi-view-action], a[target='_blank'][href], mat-expansion-panel-header, [role=tab]";

  constructor(private host: ElementRef<HTMLElement>, private state: PiReadOnlyContext, private zone: NgZone) {}

  @Input() set vexPiReadOnly(value: boolean) {
    const active = !!value;
    if (this.state.active.value === active) return;
    if (active) {
      this.state.active.next(true);
      this.events.forEach(event => this.host.nativeElement.addEventListener(event, this.blockAction, true));
      this.zone.runOutsideAngular(() => {
        this.disableNativeControls();
        this.observer = new MutationObserver(() => this.disableNativeControls());
        this.observer.observe(this.host.nativeElement, { childList: true, subtree: true, attributes: true, attributeFilter: ["disabled"] });
      });
    } else {
      this.restore();
      this.state.active.next(false);
    }
  }

  private readonly blockAction = (event: Event): void => {
    if (!this.state.active.value) return;
    const target = event.target instanceof Element ? event.target : null;
    const view = target?.closest(this.viewSelector);
    if ((event.type === "click" || event.type === "dblclick" || event.type === "keydown") && view && this.host.nativeElement.contains(view)) return;
    if (event instanceof KeyboardEvent && !["Enter", " "].includes(event.key)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  private disableNativeControls(): void {
    if (!this.state.active.value) return;
    this.originalDisabled.forEach((_disabled, control) => {
      if (!this.host.nativeElement.contains(control)) this.originalDisabled.delete(control);
    });
    this.host.nativeElement.querySelectorAll<HTMLInputElement | HTMLButtonElement | HTMLTextAreaElement | HTMLSelectElement>("input, button, textarea, select").forEach(control => {
      if (control.closest("[data-pi-view-action]")) return;
      if (!this.originalDisabled.has(control)) this.originalDisabled.set(control, control.disabled);
      if (!control.disabled) control.disabled = true;
    });
  }

  private restore(): void {
    this.observer?.disconnect();
    this.observer = null;
    this.events.forEach(event => this.host.nativeElement.removeEventListener(event, this.blockAction, true));
    this.originalDisabled.forEach((disabled, control) => { control.disabled = disabled; });
    this.originalDisabled.clear();
  }

  ngOnDestroy(): void {
    this.restore();
    this.state.active.complete();
  }
}

@Directive({ selector: "button[mat-button], button[mat-raised-button], button[mat-stroked-button], button[mat-flat-button], button[mat-icon-button]" })
export class PiReadOnlyButtonDirective implements OnInit, DoCheck, OnDestroy {
  private subscription: Subscription | null = null;
  private originallyDisabled = false;
  private restricted = false;
  constructor(@Optional() @Self() private button: MatButton, private element: ElementRef<HTMLElement>, @Optional() @SkipSelf() private state: PiReadOnlyContext) {}
  ngOnInit(): void {
    if (!this.button || !this.state || this.element.nativeElement.closest("[data-pi-view-action]")) return;
    this.subscription = this.state.active.subscribe(active => {
      if (active) {
        this.originallyDisabled = this.button.disabled;
        this.restricted = true;
        this.button.disabled = true;
      } else if (this.restricted) {
        this.button.disabled = this.originallyDisabled;
        this.restricted = false;
      }
    });
  }
  ngDoCheck(): void { if (this.restricted) this.button.disabled = true; }
  ngOnDestroy(): void { this.subscription?.unsubscribe(); }
}

/** Applies the routed parent's read-only state to actual Angular form controls. */
@Directive({ selector: "[formControlName], [formControl], [ngModel]" })
export class PiReadOnlyControlDirective implements OnInit, OnDestroy {
  private subscriptions = new Subscription();
  private wasEnabled = false;
  private disabledByPolicy = false;
  constructor(@Self() private ngControl: NgControl, private element: ElementRef<HTMLElement>, @Optional() @SkipSelf() private state: PiReadOnlyContext) {}

  ngOnInit(): void {
    if (!this.state || !this.ngControl.control || this.element.nativeElement.closest("[data-pi-view-action]")) return;
    const control = this.ngControl.control;
    this.subscriptions.add(this.state.active.subscribe(active => {
      if (active) {
        this.wasEnabled = control.enabled;
        this.disabledByPolicy = true;
        control.disable({ emitEvent: false });
      } else if (this.disabledByPolicy) {
        if (this.wasEnabled) control.enable({ emitEvent: false });
        this.disabledByPolicy = false;
      }
    }));
    this.subscriptions.add(control.statusChanges.subscribe(() => {
      if (this.state.active.value && control.enabled) control.disable({ emitEvent: false });
    }));
  }
  ngOnDestroy(): void { this.subscriptions.unsubscribe(); }
}

@NgModule({ declarations: [PiReadOnlyDirective, PiReadOnlyControlDirective, PiReadOnlyButtonDirective], exports: [PiReadOnlyDirective, PiReadOnlyControlDirective, PiReadOnlyButtonDirective] })
export class PiReadOnlyModule {}
